<template>
  <full-screen-modal v-bind:class="{ 'options submit-btn': currentPage.Template === pageTemplate.Options}"
                     :buttonLocation="getButtonLocation"
                     :buttonColor="'blue'"
                     :bgColor="'white'"
                     :submitText="$t(pageSubmitContentLocation)"
                     :hideSubmitButton="hideSubmitButton"
                     :loading="loading"
                     id="onboarding-carousel-modal"
                     @close-modal="closeModal()"
                     @modal-submitted="modalSubmitted()">
    <div slot="header"
      class="form-row center-block">
      <i18n :path="pageHeaderContentLocation"
        tag="h1"
        v-bind:class="{ 'options header-text': currentPage.Template === pageTemplate.Options }">
        <!-- Put places here if needed -->
      </i18n>
    </div>

    <div slot="subHeader" 
      class="form-row center-block" 
      :class="{'options description-text' : currentPage.Template === pageTemplate.Options}">
      <i18n :path="pageSubTextContentLocation"
        tag="p">
        <!-- Put places here if needed -->
        <span place="br"><br></span>
        <span place="br2"><br></span>
        <help-center-link
          class="text-primary"
          place="readMorePackaging"
          linkKey="dashboard/urlPackaging"
        >
          {{$t(getPageSubTextPlaceLocation('readMorePackaging'))}}
        </help-center-link>
        <help-center-link
          class="text-primary"
          place="readMoreKitting"
          linkKey="dashboard/urlKitting"
        >
          {{$t(getPageSubTextPlaceLocation('readMoreKitting'))}}
        </help-center-link>
        <help-center-link
          class="text-primary"
          place="b2bFullfillment"
          linkKey="dashboard/urlB2bFulfillment"
        >
          {{$t(getPageSubTextPlaceLocation('b2bFullfillment'))}}
        </help-center-link>
        <help-center-link
          class="text-primary"
          place="readMoreCustomBoxPreference"
          linkKey="dashboard/urlCustomBoxPreference"
        >
          {{$t(getPageSubTextPlaceLocation('readMoreCustomBoxPreference'))}}
        </help-center-link>
        <p place="fragile">&bull; {{$t(getPageSubTextPlaceLocation('fragile'))}}</p>
        <p place="hazard">&bull; {{$t(getPageSubTextPlaceLocation('hazard'))}}</p>
        <p place="expire">&bull; {{$t(getPageSubTextPlaceLocation('expire'))}}</p>
        <p place="tracking">&bull; {{$t(getPageSubTextPlaceLocation('tracking'))}}</p>
      </i18n>
    </div>

    <div slot="formContent" 
      v-if="currentPage.Template === pageTemplate.Options" 
      class="form-row center-block options radio-buttons">
      <div class="col">
        <div class="selections" :id="questionWrapperId">
          <sb-radio-buttons :selectionOptions="options"
            :selectedOption="selectedOption"
            :customComponent="radioButtonComponent"
            @select="handleSelectedOption">
          </sb-radio-buttons>
        </div>
      </div>
    </div>

    <div slot="formContent"
      v-if="currentPage.Template === pageTemplate.Options"
      class="form-row center-block options helper">
      <div class="col">
        <i18n :path="pageHelperContentLocation" 
          tag="p"
          class="helper-text">
          <!-- Put places here if needed -->
        </i18n>
      </div>
    </div>

    <button slot="extraFormButtons"
      v-if="currentPage.PageNumber > 1"
      class="btn btn-link"
      @click.prevent="back()">
      Back
    </button>

    <div slot="afterForm"
      v-if="currentPage.Template === pageTemplate.Title"
      class="form-row center-block">
      <div class="col">
        <i18n :path="pageHelperContentLocation" 
          tag="p"
          class="helper-text">
        </i18n>
        <!-- Put places here if needed -->
      </div>
    </div>

    <page-progress-indicator slot="bottomOfScreen"
      v-if="showProgressIndicator"
      :numberOfPages="numberOfQuestions"
      :pageShowing="progressIndicatorPageShowing">
    </page-progress-indicator>

  </full-screen-modal>
</template>


<script>
  import FullScreenModal from '@/components/layouts/FullScreenModal'
  import BigRadioButton from '@/components/utils/BigRadioButton'
  import PageProgressIndicator from '@/components/dashboard/utils/PageProgressIndicator'
  import PageTemplate from '@enums/modules/setup/pageTemplate'
  import messages from '@/components/dashboard/modals/AdditionalQuestionsModal.messages'

  export default {
    name: 'AdditionalQuestionsModal',
    components: {
      FullScreenModal,
      PageProgressIndicator
    },
    data () {
      return {
        radioButtonComponent: BigRadioButton,
        pageTemplate: PageTemplate,
        selectedOption: null,
        valid: true,
        loading: true
      }
    },
    created () {
      this.$shipBobStore.dispatch('setupDashboard/getAdditionalQuestionResponses').then(() => {
        this.setStartingPage()
        this.loading = false
      })
    },
    computed: {
      additionalQuestionsModel () {
        return this.$shipBobStore.getters['setupDashboard/additionalQuestionsScreenModel']
      },
      name () {
        return this.currentPage.Data.Name
      },
      pageSubmitContentLocation () {
        return this.name + '.Submit.Content'
      },
      pageHeaderContentLocation () {
        return this.name + '.Header.Content'
      },
      pageSubTextContentLocation () {
        return this.name + '.SubText.Content'
      },
      pageHelperContentLocation () {
        return this.name + '.Helper.Content'
      },
      numberOfQuestions () {
        return (this.additionalQuestionsModel.Questions) ? this.additionalQuestionsModel.Questions.length : 0
      },
      finalPageNumber () {
        return Math.max.apply(Math, this.additionalQuestionsModel.Pages.map(x => x.PageNumber))
      },
      titlePageNumber () {
        return 1
      },
      currentPage () {
        return this.additionalQuestionsModel.Pages.find(page => page.PageNumber === this.additionalQuestionsModel.CurrentPage)
      },
      currentQuestionId () {
        return this.currentPage.Data.QuestionId | null
      },
      pageShowing () {
        return (this.currentPage) ? this.currentPage.PageNumber : 0
      },
      progressIndicatorPageShowing () {
        return this.pageShowing - 1
      },
      showProgressIndicator () {
        return this.pageShowing > this.titlePageNumber
      },
      pageShowingIsFinalChecklistWithNoItems () {
        return this.pageShowing === this.finalPageNumber && !this.hasActionItems
      },
      hideSubmitButton () {
        return (this.currentPage.Template === PageTemplate.Options &&
          (!this.selectedOption || this.selectedOption.value === null))
      },
      options () {
        if (!(this.currentQuestionId && this.currentPage.Data.Buttons)) return null
        return this.currentPage.Data.Buttons.map(button => (
          {
            id: button.LocatorId,
            value: button.Value,
            icon: button.Icon,
            displayValue: this.$i18n.t(button.DisplayValue),
            description: this.$i18n.t(this.getButtonDescription(button))
          })
        )
      },
      questionWrapperId () {
        if (this.currentQuestionId) return 'question-options-' + this.currentQuestionId
        else return ''
      },
      helperTextKey () {
        return this.currentPage.Data.Helper
      },
      getButtonLocation () {
        switch (this.currentPage.PageNumber) {
          case this.titlePageNumber: return 'center'
          default: return 'right'
        }
      },
      hasActionItems () {
        return this.currentPage.Data.Checklist && this.currentPage.Data.Checklist.some(x => {
          return this.checkBoxVisible(x)
        })
      },
      validChecklist () {
        if (!this.hasActionItems) return true
        return !this.currentPage.Data.Checklist.some(x => {
          return this.checkBoxVisible(x) && !x.Checked
        })
      }
    },
    methods: {
      getButtonDescription (button) {
        return button.Description + '.Buttons.' + button.DisplayValue + '.Content'
      },
      getPageSubTextPlaceLocation (place) {
        return this.name + '.SubText.Places' + '.' + place
      },
      getPageHeaderPlaceLocation (place) {
        return this.name + '.Header.Places' + '.' + place
      },
      getPageHelperPlaceLocation (place) {
        return this.name + '.Helper.Places' + '.' + place
      },
      closeModal () {
        this.$emit('close-modal')
      },
      setCurrentQuestionResponse () {
        this.$shipBobStore.dispatch('setupDashboard/setAdditionalQuestionResponse',
          {
            questionId: this.currentQuestionId,
            response: this.selectedOption.value
          }
        )
      },
      modalSubmitted () {
        switch (this.currentPage.PageNumber) {
          case this.finalPageNumber: this.handleSubmitFinalPage(); break
          case this.titlePageNumber: this.changeCurrentPage(this.currentPage.PageNumber + 1); break
          default: this.handleSubmitQuestionPage()
        }
      },
      handleSubmitQuestionPage () {
        if (this.currentQuestionId && this.selectedOption.value !== null) {
          this.setCurrentQuestionResponse()
          this.changeCurrentPage(this.currentPage.PageNumber + 1)
        }
      },
      handleSubmitFinalPage () {
        if (this.currentPage.Template === PageTemplate.Options) {
          this.setCurrentQuestionResponse()
        }
        this.valid = this.validChecklist
        if (this.valid) this.completeProcess()
      },
      handleSelectedOption (selection) {
        this.selectedOption = selection
      },
      back () {
        this.changeCurrentPage(this.currentPage.PageNumber - 1)
      },
      changeCurrentPage (val) {
        this.$shipBobStore.dispatch('setupDashboard/setAdditionalQuestionsModalCurrentPage', val)
        this.selectedOption = (this.currentQuestionId) ? { value: this.currentPage.Data.Selected } : null
      },
      checkBoxVisible (checkBox) {
        var associatedQuestion = this.additionalQuestionsModel.Questions.find(q => q.QuestionId === checkBox.AssociatedQuestionId)
        return (associatedQuestion && associatedQuestion.Response === 1)
      },
      setCheckBox (checkBox) {
        this.$shipBobStore.dispatch('setupDashboard/setAdditionalQuestionsModalCheckBox',
          { pageNumber: this.currentPage.PageNumber, checkId: checkBox.Id, status: !checkBox.Checked })
      },
      completeProcess () {
        this.$shipBobStore.dispatch('setupDashboard/completeAdditionalQuestions')
        this.$shipBobStore.dispatch('dashboard/setDashboardSetting',
        {propertyName: 'HasCompletedOnboardingQuestions', value: true})
        this.$emit('modal-submitted')
      },
      setStartingPage () {
        var firstUnansweredQuestionPage = this.additionalQuestionsModel.Pages.find(page => {
          return (page.Data.QuestionId && page.Data.Selected === null)
        })
        if (firstUnansweredQuestionPage) {
          this.changeCurrentPage(firstUnansweredQuestionPage.PageNumber - 1)
        } else {
          this.changeCurrentPage(this.finalPageNumber)
        }
      }
    },
    i18n: { messages }
  }
</script>
<style lang="scss">
@import '@/../assets/scss/shipbob-custom/components/dashboard/_additional-questions-modal.scss';
</style>
